import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  InjectionToken,
  input,
  viewChild,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/** What the control writes back to the model. */
export type ShDatePickerValueType = 'string' | 'date';

/** Parses a model value (Date, 'yyyy-MM-dd' or an ISO date-time string) to a local Date. */
export function toPickerDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;

  // A plain date is read as a local calendar date: `new Date('2026-09-29')`
  // would be UTC midnight, which is the previous day west of Greenwich.
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (plain) {
    return new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]));
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Formats a Date as a local 'yyyy-MM-dd' string. */
export function toDateString(date: Date | null): string | null {
  if (!date) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

type PickerValue = string | Date | (Date | null)[];

/** App-wide defaults of `sh-date-picker` (provide `SH_DATE_PICKER_OPTIONS`). */
export interface ShDatePickerOptions {
  /** PrimeNG format (`yy` = 4-digit year). Default 'dd-mm-yy' (dd-MM-yyyy). */
  dateFormat?: string;
  /**
   * The typing mask: `9` is a digit, anything else is put in as is
   * (e.g. '99-99-9999'). Unset: derived from `dateFormat`; null: no mask.
   */
  inputMask?: string | null;
}

/**
 * Per-app date format and mask, e.g. for an app that shows MM/dd/yyyy:
 *   { provide: SH_DATE_PICKER_OPTIONS, useValue: { dateFormat: 'mm/dd/yy' } }
 * A field can still set its own `dateFormat` / `inputMask`.
 */
export const SH_DATE_PICKER_OPTIONS = new InjectionToken<ShDatePickerOptions>(
  'SH_DATE_PICKER_OPTIONS'
);

/**
 * The mask for a PrimeNG date format: dd and mm -> 99, yy -> 9999, y -> 99,
 * other characters as they are. Null when the format has parts of varying
 * length (d, m, month or day names), which a fixed mask can't cover.
 */
export function maskForDateFormat(format: string): string | null {
  let mask = '';
  for (let i = 0; i < format.length; ) {
    const pair = format.slice(i, i + 2);
    if (pair === 'dd' || pair === 'mm') {
      mask += '99';
      i += 2;
    } else if (pair === 'yy') {
      mask += '9999';
      i += 2;
    } else if (format[i] === 'y') {
      mask += '99';
      i += 1;
    } else if (/[dmoDM@!']/.test(format[i])) {
      return null;
    } else {
      mask += format[i];
      i += 1;
    }
  }
  return mask;
}

/**
 * Applies a mask to typed text: keeps the digits, puts the mask's other
 * characters between them, and stops when the mask is full. A literal is
 * only added once a digit follows it, so backspace over a dash works.
 */
export function applyDateMask(text: string, mask: string): string {
  const digits = text.replace(/\D/g, '');
  let out = '';
  let d = 0;
  for (const ch of mask) {
    if (d >= digits.length) break;
    out += ch === '9' ? digits[d++] : ch;
  }
  return out;
}

/**
 * Date picker over PrimeNG's `p-datepicker`. The model value is a
 * 'yyyy-MM-dd' string by default, the format the APIs take; `valueType="date"`
 * writes a `Date` instead. Incoming values may be a Date, a 'yyyy-MM-dd'
 * string or an ISO date-time. The display format is `dd-MM-yyyy` (the SHIPM8
 * date format everywhere, decided 2026-10-01) and the week
 * starts on Monday.
 *
 * `selectionMode="range"` picks a range: the value is then an array of Dates
 * (`[from, to]`, `to` still null while half chosen), whatever `valueType` is.
 *
 *   <sh-date-picker [(value)]="date" />
 *   <sh-date-picker valueType="date" formControlName="from" />
 *
 * This is the `DatePickerControl` of `@seahorse/forms`, moved onto the
 * shared form-control base (which stays untouched there for now).
 */
@Component({
  selector: 'sh-date-picker',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, FormsModule, TranslateModule, DatePickerModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShDatePickerComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-datepicker
      [ngModel]="date()"
      (ngModelChange)="onDateChange($event)"
      [ngModelOptions]="{ standalone: true }"
      [inputId]="controlId()"
      [placeholder]="placeholder() | translate"
      [ariaLabel]="ariaLabel() | translate"
      [dateFormat]="dateFormat()"
      [selectionMode]="selectionMode()"
      [showIcon]="true"
      [showButtonBar]="showButtonBar()"
      [showClear]="showClear()"
      [minDate]="minDate()"
      [maxDate]="maxDate()"
      [firstDayOfWeek]="firstDayOfWeek()"
      [disabled]="isDisabled() || isReadonly()"
      [size]="pSize()"
      [fluid]="true"
      [appendTo]="appendTo()"
      [panelStyle]="panelStyle"
      [showOnFocus]="false"
      (click)="onClick($event)"
      (onBlur)="markTouched()"
    ></p-datepicker>
  `,
})
export class ShDatePickerComponent extends ShFormControl<PickerValue> {
  // PrimeNG only sets the popup's min-width to the input width while the
  // overlay's own min-width is empty. '0' keeps the popup at its natural
  // calendar width. A constant: a new object per change detection would
  // re-render the picker endlessly.
  protected readonly panelStyle = { minWidth: '0' };
  readonly valueType = input<ShDatePickerValueType>('string');
  readonly selectionMode = input<'single' | 'range'>('single');
  private readonly _options = inject(SH_DATE_PICKER_OPTIONS, {
    optional: true,
  });

  /**
   * PrimeNG format (`yy` = 4-digit year). Default: the app's
   * `SH_DATE_PICKER_OPTIONS`, else dd-MM-yyyy, as dates display everywhere.
   */
  readonly dateFormat = input(this._options?.dateFormat ?? 'dd-mm-yy');
  /**
   * The typing mask (`9` = a digit, e.g. '99-99-9999'); null switches it off.
   * Unset: the app's `SH_DATE_PICKER_OPTIONS`, else derived from `dateFormat`
   * (twice, with ' - ' between, in range mode).
   */
  readonly inputMask = input<string | null | undefined>(undefined);
  readonly showButtonBar = input(true, { transform: booleanAttribute });
  readonly showClear = input(false, { transform: booleanAttribute });
  /**
   * Where the overlay is appended: the body by default (no clipping by
   * scrolling containers); `self` keeps it inline, which a control inside a
   * popover or drawer needs, or a click in the overlay counts as an outside
   * click and closes its container.
   */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');
  readonly minDate = input<Date | null>(null);
  readonly maxDate = input<Date | null>(null);
  /** 0 = Sunday; Monday by default (the apps are used in Europe). */
  readonly firstDayOfWeek = input(1);

  // A computed, not a getter: `p-datepicker` must get the same Date instance
  // until the value really changes (a new object per change detection
  // re-renders it endlessly). The custom `equal` keeps the previous instance
  // when a commit only echoes what the picker already shows.
  protected readonly date = computed<Date | (Date | null)[] | null>(
    () => {
      const value = this.value();
      if (this.selectionMode() === 'range') {
        return Array.isArray(value) ? value.map(toPickerDate) : null;
      }
      return toPickerDate(value);
    },
    { equal: (a, b) => sameDates(a, b) }
  );

  protected readonly effectiveMask = computed<string | null>(() => {
    const own = this.inputMask();
    const app = this._options?.inputMask;
    const mask =
      own !== undefined
        ? own
        : app !== undefined
        ? app
        : maskForDateFormat(this.dateFormat());
    if (!mask) return null;
    // PrimeNG writes a range as 'from - to'.
    return this.selectionMode() === 'range' ? `${mask} - ${mask}` : mask;
  });

  constructor() {
    super();
    // Capture phase on the host: the text is masked before PrimeNG's own
    // input handler parses it.
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const onInput = (event: Event) => this.maskInput(event);
    host.addEventListener('input', onInput, true);
    inject(DestroyRef).onDestroy(() =>
      host.removeEventListener('input', onInput, true)
    );
  }

  private maskInput(event: Event): void {
    const mask = this.effectiveMask();
    const el = event.target;
    if (!mask || !(el instanceof HTMLInputElement)) return;

    const caret = el.selectionStart ?? el.value.length;
    const digitsBeforeCaret = el.value
      .slice(0, caret)
      .replace(/\D/g, '').length;
    const masked = applyDateMask(el.value, mask);
    if (masked === el.value) return;

    el.value = masked;
    // Keep the caret behind the same digit it was behind.
    let pos = 0;
    for (let seen = 0; pos < masked.length && seen < digitsBeforeCaret; pos++) {
      if (/\d/.test(masked[pos])) seen++;
    }
    el.setSelectionRange(pos, pos);
  }

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );

  protected override equals(a: PickerValue | null, b: PickerValue | null) {
    return sameDates(
      Array.isArray(a) ? a : toPickerDate(a),
      Array.isArray(b) ? b : toPickerDate(b)
    );
  }

  private readonly picker = viewChild(DatePicker);

  /**
   * The calendar opens on a click in the input, not on focus: a dialog
   * focuses its first field when it opens, and a date there would pop its
   * calendar over the form. The icon button toggles it as before.
   */
  protected onClick(event: MouseEvent): void {
    const picker = this.picker();
    if (
      event.target instanceof HTMLInputElement &&
      picker &&
      !picker.overlayVisible &&
      !picker.disabled
    ) {
      picker.showOverlay();
    }
  }

  protected onDateChange(picked: Date | (Date | null)[] | null): void {
    if (this.selectionMode() === 'range') {
      this.commit(Array.isArray(picked) && picked.length ? picked : null);
      return;
    }
    const next = picked instanceof Date ? picked : null;
    this.commit(this.valueType() === 'date' ? next : toDateString(next));
  }
}

function sameDates(
  a: Date | (Date | null)[] | null,
  b: Date | (Date | null)[] | null
): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    return (
      a.length === b.length &&
      a.every((d, i) => toDateString(d) === toDateString(b[i]))
    );
  }
  return toDateString(a) === toDateString(b);
}
